import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraduationCap, LogOut, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useSettingsStore from '../../store/settingsStore';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import LanguageSwitcher from '../LanguageSwitcher';

// One configurable shell behind all four role layouts (Admin/SuperAdmin use
// `variant="sidebar"`, Proctor/Student use `variant="navbar"`) — replaces four
// independently hand-duplicated layout files that drifted on container width,
// role-badge theming, avatar style, default brand mark, and had no mobile nav.
//
// Both variants render as floating, rounded panels on a neutral page canvas
// (sidebar/header separate from the content pane) rather than edge-to-edge
// bars, per the "UI Design" reference the admin asked to match.
//
// `chromeTone` colors the sidebar/navbar bar itself; `contentTone` colors the
// content pane and drives the violet (dark) vs brand (light) accent used by
// the role badge, avatar, and (for the sidebar variant) the sidebar's own
// gradient — dark content is the deliberate SuperAdmin "control plane" look,
// with its own fixed violet gradient independent of a tenant's configured
// brand color; light content uses the institution's configured primary color.
const AppShell = ({
  variant = 'sidebar',
  chromeTone = 'dark',
  contentTone = 'light',
  navItems = [],
  roleLabel,
  headerTitle,
  showLanguageSwitcher = false,
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { portalName, logoUrl } = useSettingsStore();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDarkContent = contentTone === 'dark';
  // The sidebar/navbar bar is "colored" (brand gradient or fixed violet) only
  // in its dark-chrome form -- a hypothetical light-chrome sidebar (unused by
  // any current role) stays a plain white panel instead.
  const sidebarColored = chromeTone === 'dark';

  const brandMark = (size = 'w-8 h-8', iconSize = 'w-5 h-5', onColoredBg = false) => (
    <div className={[size, onColoredBg ? 'bg-white/15' : 'bg-themePrimary', 'rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0'].join(' ')}>
      {logoUrl ? (
        <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" />
      ) : (
        <GraduationCap className={[iconSize, 'text-white'].join(' ')} />
      )}
    </div>
  );

  // Page canvas behind the floating panels.
  const outerBg = isDarkContent ? 'bg-black' : 'bg-slate-200';
  // The content pane (header + main for sidebar variant; header and main as
  // separate panels for navbar variant).
  const paneBg = isDarkContent ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200';
  const paneShadow = isDarkContent ? 'shadow-xl shadow-black/20' : 'shadow-lg shadow-slate-900/5';
  const headerBorder = isDarkContent ? 'border-slate-800' : 'border-slate-100';
  const textColor = isDarkContent ? 'text-white' : 'text-slate-800';

  // Sidebar surface: SuperAdmin keeps its own fixed violet gradient
  // (deliberately independent of tenant branding — ops tooling shouldn't
  // re-skin per-institution); everyone else gets a gradient of the
  // institution's configured primary color via CSS color-mix().
  const sidebarClassName = sidebarColored
    ? (isDarkContent ? 'bg-gradient-to-b from-violet-700 to-violet-950 text-white' : 'text-white')
    : 'bg-white border border-slate-200 text-slate-800';
  const sidebarStyle = sidebarColored && !isDarkContent
    ? { backgroundImage: 'linear-gradient(165deg, var(--theme-primary), color-mix(in srgb, var(--theme-primary) 55%, black))' }
    : undefined;

  const chromeBorder = sidebarColored ? 'border-white/10' : 'border-slate-200';
  const navInactive = sidebarColored ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';
  const navActive = sidebarColored ? 'bg-white/20 text-white shadow-sm' : 'bg-themePrimary text-white';
  // The top navbar variant's own bar stays a plain light/dark surface (never
  // brand-gradient), so its active pill can safely use the brand color.
  const navbarNavInactive = isDarkContent ? 'text-slate-300 hover:bg-white/5 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';
  const navbarNavActive = 'bg-themePrimary text-white';

  // ─── Sidebar variant (Admin, SuperAdmin) ─────────────────────────────────
  if (variant === 'sidebar') {
    return (
      <div className={['h-screen w-full overflow-hidden flex p-3 md:p-4 gap-3 md:gap-4', outerBg].join(' ')}>
        {/* Mobile backdrop */}
        {mobileOpen && (
          <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileOpen(false)} />
        )}

        <aside
          style={sidebarStyle}
          className={[
            'flex flex-col flex-shrink-0 transition-all duration-300 z-40 rounded-2xl shadow-lg',
            sidebarClassName,
            'fixed inset-y-3 left-3 md:static md:inset-auto',
            mobileOpen ? 'translate-x-0' : '-translate-x-[200%] md:translate-x-0',
            sidebarOpen ? 'w-64' : 'w-16',
          ].join(' ')}
        >
          <div className={['flex items-center justify-between h-16 px-4 border-b flex-shrink-0', chromeBorder].join(' ')}>
            {sidebarOpen && (
              <div className="flex items-center gap-2 min-w-0">
                {brandMark('w-9 h-9', 'w-5 h-5', sidebarColored)}
                <span className="font-bold text-lg truncate max-w-[140px]">{portalName || 'Exam Portal'}</span>
              </div>
            )}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="hidden md:block p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setMobileOpen(false)}
              className="md:hidden p-1 rounded hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 py-4 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  ['flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-colors duration-200 mb-1', isActive ? navActive : navInactive].join(' ')
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="font-medium">{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          <div className={['border-t p-4 flex-shrink-0', chromeBorder].join(' ')}>
            {sidebarOpen && (
              <div className="flex items-center gap-3 mb-3 min-w-0">
                <Avatar name={user?.name} tone={contentTone} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || roleLabel}</p>
                  <p className={['text-xs truncate', sidebarColored ? 'text-white/50' : 'text-slate-500'].join(' ')}>
                    {user?.email || user?.username || ''}
                  </p>
                </div>
              </div>
            )}
            <button onClick={handleLogout} className={['flex items-center gap-2 rounded-lg px-2 py-2 w-full transition-colors', navInactive].join(' ')}>
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        </aside>

        <div className={['flex-1 flex flex-col overflow-hidden rounded-2xl', paneBg, paneShadow].join(' ')}>
          <header className={['h-16 flex items-center px-4 md:px-6 flex-shrink-0 gap-3 border-b', headerBorder, textColor].join(' ')}>
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 -ml-1 rounded hover:bg-black/5" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold truncate">{headerTitle || portalName || 'Exam Portal'}</h1>
            <div className="ml-auto flex items-center gap-3">
              <Badge variant="brand" tone={contentTone} className="hidden sm:inline-flex">{roleLabel}</Badge>
              <span className={['text-sm hidden sm:inline', isDarkContent ? 'text-white/70' : 'text-slate-600'].join(' ')}>
                {user?.name || roleLabel}
              </span>
              <Avatar name={user?.name} tone={contentTone} size="sm" />
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  // ─── Navbar variant (Proctor, Student) ───────────────────────────────────
  return (
    <div className={['min-h-screen flex flex-col p-3 md:p-4 gap-3 md:gap-4', outerBg].join(' ')}>
      <header className={['flex-shrink-0 rounded-2xl', paneBg, paneShadow, textColor].join(' ')}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-6 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                {brandMark()}
                <span className="font-bold text-lg truncate max-w-[140px]">{portalName || t('common.appName')}</span>
              </div>
              {navItems.length > 0 && (
                <nav className="hidden md:flex items-center gap-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        ['px-4 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? navbarNavActive : navbarNavInactive].join(' ')
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              )}
            </div>

            <div className="flex items-center gap-3">
              {showLanguageSwitcher && (
                <LanguageSwitcher tone="navbar" className="hidden sm:inline-flex" />
              )}
              <div className="hidden sm:flex items-center gap-2">
                <Avatar name={user?.name} tone={contentTone} size="sm" />
                <div>
                  <p className="text-sm font-medium">{user?.name || roleLabel}</p>
                  <p className={['text-xs', isDarkContent ? 'text-white/50' : 'text-slate-500'].join(' ')}>{roleLabel}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className={['flex items-center gap-1 text-sm px-3 py-2 rounded-lg transition-colors', navbarNavInactive].join(' ')}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t('common.logout')}</span>
              </button>
              {navItems.length > 0 && (
                <button
                  onClick={() => setMobileOpen((v) => !v)}
                  className={['md:hidden p-2 rounded-lg transition-colors', navbarNavInactive].join(' ')}
                  aria-label={t('common.toggleMenu')}
                >
                  {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              )}
            </div>
          </div>

          {mobileOpen && navItems.length > 0 && (
            <nav className="md:hidden pb-3 flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    ['px-4 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? navbarNavActive : navbarNavInactive].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>
      </header>

      <main className={['flex-1 overflow-y-auto rounded-2xl', paneBg, paneShadow].join(' ')}>
        <Outlet />
      </main>
    </div>
  );
};

export default AppShell;
